import { escapeHtml } from '@app/shared/utils/helper';

/**
 * HTML Templates for dynamic content generation.
 * Classes are styled via ::ng-deep .value-content in meeting-audit-log.component.scss
 * so Tailwind utilities apply correctly to innerHTML content.
 */
export const TEMPLATES = {
  nextStepWithAssignee: (index: number, assignee: string, action: string) =>
    `<div class="audit-step"><span class="audit-num">${index}.</span> <strong class="audit-assignee">${assignee}:</strong> ${action}</div>`,

  nextStepSimple: (index: number, content: string) => `<div class="audit-step"><span class="audit-num">${index}.</span> ${content}</div>`,

  summaryDetailItem: (index: number, label: string, summary: string, isLast: boolean) =>
    `<div class="audit-detail${isLast ? ' audit-detail--last' : ''}">
      <div class="audit-detail-label">${index}. ${label}</div>
      <div class="audit-detail-summary">${summary}</div>
    </div>`,

  showMoreToggle: (remaining: number) => `<div class="audit-more">... and ${remaining} more items (export to CSV to see all)</div>`,

  sectionHeader: (title: string) => `<div class="audit-section-header">${title}</div>`,

  eventType: (type: string) =>
    `<div class="audit-section">
      ${TEMPLATES.sectionHeader('Event Type')}
      <div class="audit-event-type">${type}</div>
    </div>`,

  meetingDetails: (topic: string, id: string, email: string) =>
    `<div class="audit-meeting-box">
      <div class="audit-section-header audit-meeting-box-header">Meeting Details</div>
      <div class="audit-meeting-topic">${topic}</div>
      ${id ? `<div class="audit-meeting-meta">ID: ${id}</div>` : ''}
      ${email ? `<div class="audit-meeting-meta">Host: ${email}</div>` : ''}
    </div>`,

  timing: (startTime: string, endTime: string) =>
    `<div class="audit-section">
      ${TEMPLATES.sectionHeader('Timing')}
      ${startTime ? `<div class="audit-text-sm"><span class="audit-num">Start:</span> ${startTime}</div>` : ''}
      ${endTime ? `<div class="audit-text-sm"><span class="audit-num">End:</span> ${endTime}</div>` : ''}
    </div>`,

  summaryOverview: (summary: string) =>
    `<div class="audit-section">
      ${TEMPLATES.sectionHeader('Summary')}
      <div class="audit-summary-text">${summary}</div>
    </div>`,

  section: (title: string, content: string) =>
    `<div class="audit-section">
      ${TEMPLATES.sectionHeader(title)}
      <div class="audit-text-sm">${content}</div>
    </div>`,

  externalLink: (url: string, text: string) =>
    `<div>
      <a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="audit-link">
        ${escapeHtml(text)}
      </a>
    </div>`,
} as const;

export const UNIX_TIMESTAMP_ATTRIBUTES = new Set(['last_end_time', 'last_invite_received_time', 'last_invite_delivered_time', 'last_invite_bounced_time']);

export const MAX_DISPLAY_ITEMS = 5;

/** 2000-01-01 — prevents false positives with arbitrary numbers */
export const MIN_TIMESTAMP = 946684800;

/** 2200-12-31 — future-proof for long-term scheduling */
export const MAX_TIMESTAMP = 7289654399;

/** Attributes that are actual URLs and should be rendered as clickable links */
export const URL_ATTRIBUTES = new Set([
  'join_url',
  'start_url',
  'registration_url',
  'public_link',
  'ai_summary_url',
  'youtube_link',
  'link',
  'profile_picture',
]);

/** System-generated attributes that always show "System" as the user */
export const SYSTEM_GENERATED_ATTRIBUTES = new Set([
  'meeting_id',
  'proj_id',
  'user_id',
  'host_key',
  'join_url',
  'passcode',
  'password',
  'last_end_time',
  'ics_uid_timezone',
  'ics_additional_uids',
  'updated_by_list',
  'last_bulk_registrant_job_status',
  'last_bulk_registrants_job_failed_count',
  'last_bulk_registrants_job_warning_count',
  'last_mailing_list_members_sync_job_status',
  'last_mailing_list_members_sync_job_failed_count',
  'last_mailing_list_members_sync_job_warning_count',
]);

/** Attributes hidden by default — user must explicitly select them in filters to show */
export const HIDDEN_BY_DEFAULT_ATTRIBUTES = new Set([
  'last_invite_delivered_time',
  'last_invite_received_time',
  'last_bulk_registrant_job_status',
  'last_invite_received_message_id',
  'last_invite_delivery_successful',
  'last_invite_bounced',
  'last_invite_bounced_time',
  'last_invite_bounced_type',
  'last_invite_bounced_sub_type',
  'last_invite_bounced_diagnostic_code',
  'last_bulk_registrants_job_failed_count',
  'last_bulk_registrants_job_warning_count',
  'last_mailing_list_members_sync_job_status',
  'last_mailing_list_members_sync_job_failed_count',
  'last_mailing_list_members_sync_job_warning_count',
  'case_insensitive_email',
  'updated_by_list',
  '_registrant_snapshot',
  'meeting_and_occurrence_id',
  'ics_uid_timezone',
  'ics_additional_uids',
  'use_unique_ics_uid',
]);

/** Attribute categories for conditional display sections */
export const ARTIFACT_ATTRIBUTES = new Set(['category', 'link', 'artifacts']);
export const RECORDING_ATTRIBUTES = new Set(['recording_enabled', 'recording_access', 'transcript_enabled', 'transcript_access', 'youtube_upload_enabled']);
export const SUMMARY_ATTRIBUTES = new Set(['zoom_ai_enabled', 'require_ai_summary_approval', 'ai_summary_access']);
export const SYSTEM_USER_IDS = new Set(['events', 'webhooks', 'scripts']);
export const SYSTEM_USER_NAMES = new Set(['Zoom Events', 'Zoom Webhooks', 'Zoom Scripts']);
